import PropTypes from "prop-types";
import React, { useMemo } from "react";

import { Flex, P, StyledLink } from "orcs-design-system";
import {
  heading,
  noOptionsLabel,
  noOptionsLinkLabel,
  searchErrorLabel,
} from "src/components/Search/Search.config";
import URL, { url, getSearchPath } from "src/consts/urlPaths";
import { createPlaceholderData } from "src/util/search";
import { filterTypes } from "../SearchPage.config";

import SearchResults from "./SearchResults";

const DISPLAY_ALL_MAX = 10;
const PLACEHOLDER_COUNT = 3;

const Results = ({
  isLoadingPeople,
  isLoadingTeams,
  isLoadingTags,
  onLoadMore,
  formattedData,
  params,
  workspace,
  compactViewMore = false,
  showExploreLink = false,
  searchError,
  emptyStateComponent,
}) => {
  const isAllSearch = params.type === filterTypes.ALL;
  const isPeopleSearch = params.type === filterTypes.PEOPLE;
  const isTeamSearch = params.type === filterTypes.TEAMS;
  const isTagSearch = params.type === filterTypes.TAGS;

  const peopleOptions = formattedData?.people?.options ?? [];
  const teamOptions = formattedData?.teams?.options ?? [];
  const tagOptions = formattedData?.tags?.options ?? [];

  const peopleFound = formattedData?.people?.counters?.found ?? 0;
  const peopleTotal = formattedData?.people?.counters?.total ?? 0;
  const teamFound = formattedData?.teams?.counters?.found ?? 0;
  const teamTotal = formattedData?.teams?.counters?.total ?? 0;
  const tagFound = formattedData?.tags?.counters?.found ?? 0;
  const tagTotal = formattedData?.tags?.counters?.total ?? 0;

  const { searchTerm } = params;

  const displayLimit = isAllSearch ? DISPLAY_ALL_MAX : undefined;

  const resultsSets = [];

  const placeholders = useMemo(() => {
    const data = createPlaceholderData(PLACEHOLDER_COUNT).map((p) =>
      p.options.map((o) => ({
        ...o,
        isPlaceholder: true,
      }))
    );

    return {
      people: data[0],
      teams: data[1],
      tags: data[2],
    };
  }, []);

  if (isAllSearch || isPeopleSearch) {
    const collection = isLoadingPeople
      ? [...peopleOptions, ...placeholders.people]
      : peopleOptions;

    resultsSets.push(
      <SearchResults
        key="people"
        header={heading.people}
        collection={collection}
        isLoading={isLoadingPeople}
        onLoadMore={onLoadMore}
        selectedString={searchTerm}
        foundCount={peopleFound}
        totalCount={peopleTotal}
        limitCount={displayLimit}
        viewMoreURL={getSearchPath(filterTypes.PEOPLE, searchTerm)}
        workspace={workspace}
        compactViewMore={compactViewMore}
      />
    );
  }

  if (isAllSearch || isTeamSearch) {
    const collection = isLoadingTeams
      ? [...teamOptions, ...placeholders.teams]
      : teamOptions;

    resultsSets.push(
      <SearchResults
        key="teams"
        header={heading.teams}
        collection={collection}
        isLoading={isLoadingTeams}
        onLoadMore={onLoadMore}
        selectedString={searchTerm}
        foundCount={teamFound}
        totalCount={teamTotal}
        limitCount={displayLimit}
        viewMoreURL={getSearchPath(filterTypes.TEAMS, searchTerm)}
        workspace={workspace}
        compactViewMore={compactViewMore}
      />
    );
  }

  if (isAllSearch || isTagSearch) {
    const collection = isLoadingTags
      ? [...tagOptions, ...placeholders.tags]
      : tagOptions;

    resultsSets.push(
      <SearchResults
        key="tags"
        header={heading.tags}
        collection={collection}
        isLoading={isLoadingTags}
        onLoadMore={onLoadMore}
        selectedString={searchTerm}
        foundCount={tagFound}
        totalCount={tagTotal}
        limitCount={displayLimit}
        viewMoreURL={getSearchPath(filterTypes.TAGS, searchTerm)}
        workspace={workspace}
        compactViewMore={compactViewMore}
      />
    );
  }

  const showEmptyState =
    formattedData == null &&
    !isLoadingPeople &&
    !isLoadingTeams &&
    !isLoadingTags;

  if (showEmptyState) {
    if (emptyStateComponent) {
      return (
        <Flex justifyContent="center" p="s">
          {emptyStateComponent}
        </Flex>
      );
    }

    const label = searchError ? (
      searchErrorLabel
    ) : (
      <>
        {noOptionsLabel}{" "}
        <StyledLink
          fontSize="1"
          to={showExploreLink ? url(URL.EXPLORE) : url(URL.TEAMS)}
        >
          {noOptionsLinkLabel}
        </StyledLink>
      </>
    );

    return (
      <Flex justifyContent="center" p="s">
        <P color="black" fontSize="1">
          {label}
        </P>
      </Flex>
    );
  }

  if (resultsSets.length > 0) {
    return resultsSets;
  }

  return null;
};

Results.propTypes = {
  isLoadingPeople: PropTypes.bool,
  isLoadingTeams: PropTypes.bool,
  isLoadingTags: PropTypes.bool,
  onLoadMore: PropTypes.func,
  formattedData: PropTypes.object,
  params: PropTypes.shape({
    type: PropTypes.string,
    searchTerm: PropTypes.string,
  }),
  workspace: PropTypes.object,
  compactViewMore: PropTypes.bool,
  showExploreLink: PropTypes.bool,
  searchError: PropTypes.object,
  emptyStateComponent: PropTypes.node,
};

export default Results;
